// Shared MultiRecord++ targets and capture metadata, plus the web recorder's
// silence rules. Native uses its existing time/size limits and keeps nonempty audio.
export const CHUNK_MIN_MS = 120000;
export const CHUNK_HARD_MS = 140000;
export const CHUNK_SILENCE_MS = 750;
export const MIN_RETARGET_CUT_MS = 5000;
export const MIN_VOICED_MS = 750;

const LOG = '[MR++] [Recorder]';

export class MultiRecordSession {
  constructor() {
    this.targets = [];
    this.chunkStartedAt = 0;
    this.chunkTargets = [];
    this.lastVoiceAt = 0;
    this.voicedMs = 0;
    this.retargetPending = false;
    this.keepShortCurrentChunk = false;
    this.analyserProven = false;
    this.meteringMissing = false;
    this.recordingSessionId = null;
    this.sessionStartedAt = 0;
    this.chunkSeq = 0;
    this.visitJoins = {};
  }

  _startRecordingSession(targets) {
    this.targets = [...(targets || [])];
    this.recordingSessionId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `mr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    this.sessionStartedAt = Date.now();
    this.chunkSeq = 0;
    this.visitJoins = {};
    // Trust must be earned again when a new capture session starts. Otherwise
    // failed metering in a later session could discard every chunk as silence.
    this.analyserProven = false;
    this._noteVisitJoins(this.targets);
    console.log(
      `${LOG} recording session ${this.recordingSessionId} started with [${this.targets}]`,
    );
  }

  _noteVisitJoins(targets) {
    if (!this.recordingSessionId) return;
    (targets || []).forEach((id) => {
      const key = String(id);
      if (this.visitJoins[key] === undefined) {
        this.visitJoins[key] = Date.now() - this.sessionStartedAt;
      }
    });
  }

  updateTargets(targets, recording) {
    const next = [...(targets || [])];
    const same =
      next.length === this.targets.length &&
      next.every((id, index) => id === this.targets[index]);
    if (same) return null;
    const previous = this.targets;
    this.targets = next;
    this._noteVisitJoins(next);
    if (!recording) {
      console.log(
        `${LOG} targets changed [${previous}] -> [${next}] while stopped`,
      );
      return null;
    }
    const matchesChunk =
      next.length === this.chunkTargets.length &&
      next.every((id, index) => id === this.chunkTargets[index]);
    if (matchesChunk) {
      this.retargetPending = false;
      console.log(
        `${LOG} targets returned to [${next}]; deferred cut cancelled`,
      );
      return null;
    }

    const chunkAgeMs = Date.now() - this.chunkStartedAt;
    if (chunkAgeMs < MIN_RETARGET_CUT_MS) {
      // Keep targets frozen until the five-second guard expires. Unproven
      // metering cannot establish that an apparently quiet chunk has no speech.
      this.retargetPending = true;
      console.log(
        `${LOG} retarget to [${next}] deferred — chunk is only ${chunkAgeMs}ms old; cut pending at ${MIN_RETARGET_CUT_MS}ms`,
      );
      return null;
    }
    this.retargetPending = false;
    console.log(
      `${LOG} targets changed [${previous}] -> [${next}]; cutting current chunk`,
    );
    return 'target change';
  }

  beginChunk(now = Date.now()) {
    this.chunkStartedAt = now;
    this.lastVoiceAt = this.chunkStartedAt;
    this.voicedMs = 0;
    this.retargetPending = false;
    this.keepShortCurrentChunk = false;
    this.meteringMissing = false;
    this.chunkTargets = [...this.targets];
  }

  recordSpeech(speaking, elapsedMs) {
    if (speaking == null) {
      this.meteringMissing = true;
      this.lastVoiceAt = Date.now();
      return;
    }
    if (!speaking) return;
    this.lastVoiceAt = Date.now();
    this.voicedMs += elapsedMs;
    this.analyserProven = true;
  }

  retargetCutReason() {
    if (
      this.retargetPending &&
      Date.now() - this.chunkStartedAt >= MIN_RETARGET_CUT_MS
    ) {
      this.retargetPending = false;
      return 'deferred target change';
    }
    return null;
  }

  cutReason() {
    const retarget = this.retargetCutReason();
    if (retarget) return retarget;
    const now = Date.now();
    const age = now - this.chunkStartedAt;
    const silence = now - this.lastVoiceAt;
    // After two minutes, prefer a sentence gap, with a hard 140-second ceiling.
    if (age >= CHUNK_HARD_MS) return 'hard limit';
    if (age >= CHUNK_MIN_MS && silence >= CHUNK_SILENCE_MS) {
      return 'silence window';
    }
    return null;
  }

  prepareCut(reason) {
    this.keepShortCurrentChunk =
      reason === 'target change' || reason === 'deferred target change';
  }

  shouldKeepChunk() {
    return (
      this.voicedMs >= MIN_VOICED_MS ||
      !this.analyserProven ||
      this.meteringMissing ||
      (this.keepShortCurrentChunk && this.voicedMs > 0)
    );
  }

  // Call once for a nonempty chunk being saved, before any upload or retry.
  chunkMetadata(now = Date.now()) {
    this.chunkSeq += 1;
    return {
      targets: [...this.chunkTargets],
      durationSec: Math.max(
        1,
        Math.round((now - this.chunkStartedAt) / 1000),
      ),
      recordingSessionId: this.recordingSessionId,
      chunkSeq: this.chunkSeq,
      chunkOffsetMs: Math.max(0, this.chunkStartedAt - this.sessionStartedAt),
      visitJoins: { ...this.visitJoins },
    };
  }
}

export default MultiRecordSession;
